import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeOff, UserPlus, CheckCircle, XCircle, CreditCard } from "lucide-react";
import axios from "axios";

const API = `${window.location.origin}/api`;

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    centro_estetico: "",
    indirizzo_centro: "",
    referente_id: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referenteStatus, setReferenteStatus] = useState(null); // null, 'checking', 'valid', 'invalid'
  const [referenteInfo, setReferenteInfo] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateReferenteId = async (id) => {
    if (!id || id.length < 5) {
      setReferenteStatus(null);
      setReferenteInfo(null);
      return;
    }
    
    setReferenteStatus('checking');
    try {
      const response = await axios.get(`${API}/referenti/validate/${id.toUpperCase()}`);
      if (response.data.valid) {
        setReferenteStatus('valid');
        setReferenteInfo(response.data);
      } else {
        setReferenteStatus('invalid');
        setReferenteInfo(null);
      }
    } catch (error) {
      setReferenteStatus('invalid');
      setReferenteInfo(null);
    }
  };

  const handleReferenteChange = (e) => {
    const value = e.target.value.toUpperCase();
    setFormData({ ...formData, referente_id: value });
    
    // Debounce validation
    clearTimeout(window.referenteTimeout);
    window.referenteTimeout = setTimeout(() => {
      validateReferenteId(value);
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData);
      toast.success("Registrazione completata! Benvenuta!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex items-center justify-center p-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo ODR */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_beauty-wellness-31/artifacts/42tsb1mv_PdfImage.png"
            alt="ODR Accademia"
            className="h-24 mx-auto mb-4"
          />
          <h1 className="font-serif text-2xl font-semibold text-[#B87A8A]">La Bellezza che Cura</h1>
          <p className="text-muted-foreground mt-1">Formazione in Estetica Oncologica</p>
        </div>

        <Card className="rounded-2xl shadow-xl border-border/50">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-serif text-2xl">Registrati</CardTitle>
            <CardDescription>Crea il tuo account per accedere ai corsi</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              <div className="space-y-2">
                <Label htmlFor="register-name">Nome e Cognome *</Label>
                <Input
                  id="register-name"
                  name="register_name"
                  type="text"
                  placeholder="Maria Rossi"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="register-name-input"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-email">Email *</Label>
                <Input
                  id="register-email"
                  name="register_email"
                  type="email"
                  placeholder="nome@esempio.it"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="register-email-input"
                  autoComplete="new-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="register-password">Password *</Label>
                <div className="relative">
                  <Input
                    id="register-password"
                    name="register_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    className="rounded-xl pr-10"
                    data-testid="register-password-input"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefono *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+39 333 1234567"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="register-phone-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="centro_estetico">Nome Centro Estetico *</Label>
                <Input
                  id="centro_estetico"
                  name="centro_estetico"
                  type="text"
                  placeholder="Beauty Center Milano"
                  value={formData.centro_estetico}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="register-centro-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="indirizzo_centro">Indirizzo Centro *</Label>
                <Input
                  id="indirizzo_centro"
                  name="indirizzo_centro"
                  type="text"
                  placeholder="Via Roma 1, 20100 Milano"
                  value={formData.indirizzo_centro}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="register-indirizzo-input"
                />
              </div>

              {/* ID Referente (Distributore/Agente) - OBBLIGATORIO */}
              <div className="space-y-2">
                <Label htmlFor="referente_id" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Codice ID Referente *
                </Label>
                <div className="relative">
                  <Input
                    id="referente_id"
                    name="referente_id"
                    type="text"
                    placeholder="es. ODR-B01-PI01 oppure ODR"
                    value={formData.referente_id}
                    onChange={handleReferenteChange}
                    required
                    className={`rounded-xl pr-10 uppercase ${
                      referenteStatus === 'valid' ? 'border-green-500 focus:border-green-500' :
                      referenteStatus === 'invalid' ? 'border-red-500 focus:border-red-500' : ''
                    }`}
                    data-testid="register-referente-input"
                  />
                  {referenteStatus === 'checking' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin inline-block" />
                    </div>
                  )}
                  {referenteStatus === 'valid' && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                  )}
                  {referenteStatus === 'invalid' && (
                    <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
                {referenteStatus === 'valid' && referenteInfo && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    {referenteInfo.ruolo}: {referenteInfo.ragione_sociale || referenteInfo.nome} ({referenteInfo.regione})
                  </p>
                )}
                {referenteStatus === 'invalid' && (
                  <p className="text-sm text-red-500">Codice non valido. Verifica il codice sulla tua card ODR.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Inserisci il codice presente sulla card ODR. Se sei un collaboratore ODR, inserisci <strong>ODR</strong>.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full btn-shine"
                disabled={loading}
                data-testid="register-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Registrazione in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Registrati
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Hai già un account? </span>
              <Link
                to="/login"
                className="text-primary hover:underline font-medium"
                data-testid="login-link"
              >
                Accedi
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
